import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NavBar, NavBarTab } from '@rossum/components/nav-bar/NavBar';
import { boldText } from '@rossum/components/variable-suggestions/boldText';
import { IconFlask, IconTestPipe2 } from '@rossum/ui/icons/tabler';
import { Box, Chip, Slide, Stack, SvgIcon, Tooltip } from '@rossum/ui/material';
import { isDevFeatureEnabled } from '../../constants/dev';
import CallToActionButton from '../../containers/CallToAction';
import { useBillingEnabled } from '../../features/billing';
import { PricingBanner } from '../../features/pricing/components/PricingBanner';
import { usePricingBannerWarning } from '../../features/pricing/hooks/usePricingBannerWarning';
import { useQuickSearchContext } from '../../features/quick-search/QuickSearchContext';
import { UserPanel } from '../../features/user-panel';
import {
  isTrialSelector,
  safeOrganizationSelector,
} from '../../redux/modules/organization/selectors';
import {
  currentTabSelector,
  hideAppBarSelector,
} from '../../redux/modules/router/selectors';
import { State } from '../../types/state';
import PoweredBy from '../UI/PoweredBy';
import { useNavBarVisibility } from './hooks/useNavBarVisibility';
import { useVisibleTabs } from './hooks/useVisibleTabs';
import Logo, { hasCustomLogoSelector } from './Logo';
import { Tab, TabName, tabToLink } from './navigationStructure';
import PopperMenu from './PopperMenu';
import { SearchButton } from './SearchButton';
import { ToggleNavBarButton } from './ToggleNavBarButton';

const NavBarContent = () => {
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLElement) | null>(
    null
  );
  const [hoveredTab, setHoveredTab] = useState<Tab | null>(null);

  const { isVisible, toggleVisibility, isFeatureEnabled } =
    useNavBarVisibility();

  const menuOpen = Boolean(anchorEl);

  const { push } = useHistory();
  const { setOpen } = useQuickSearchContext();

  const currentPathsTab = useSelector(currentTabSelector);
  const organization = useSelector(safeOrganizationSelector);

  const intl = useIntl();

  const visibleTabs = useVisibleTabs();

  // the user might refresh the page in annotations path, and annotations might be temporarily excluded from the visible paths.
  // To make MUI happy here we make sure that the currentTab value being passed is always visible.
  const currentTab = visibleTabs.find(
    tab => tab.name === currentPathsTab?.name
  )?.name;

  const tabs = useMemo<NavBarTab<TabName>[]>(
    () =>
      visibleTabs.map(tab => ({
        name: tab.name,
        label: intl.formatMessage({ id: `components.appBar.${tab.name}` }),
        to: tabToLink(tab),
        // TODO @unified-dashboard: temporarily disable external url mutations when on /documents route https://coupadev.atlassian.net/browse/AC-4257
        disabled: currentTab === 'documents' && tab.name === 'documents',
        'data-cy': `${tab.name}-navtab`,
      })),
    [visibleTabs, intl, currentTab]
  );

  const openSearch = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleTabHoverEnter = useCallback(
    (tab: NavBarTab, anchor: HTMLElement) => {
      const original = visibleTabs.find(t => t.name === tab.name);
      if (!original) return;
      setAnchorEl(anchor);
      setHoveredTab(original);
    },
    [visibleTabs]
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setHoveredTab(null);
  }, []);

  const handleNavigation = useCallback(
    (e: React.MouseEvent, pathname: string) => {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      handleMenuClose();
      push(pathname);
    },
    [handleMenuClose, push]
  );

  const handleTabClick = useCallback(
    (tab: NavBarTab, e: React.MouseEvent) => {
      handleNavigation(e, tab.to);
    },
    [handleNavigation]
  );

  const isTrial = useSelector((state: State) => isTrialSelector(state));

  const hasCustomLogo = useSelector(hasCustomLogoSelector);

  const bannerWarning = usePricingBannerWarning();
  const { isEnabled: billingEnabled } = useBillingEnabled();

  const badges = (
    <Stack direction="row" sx={{ px: 1 }} spacing={0.5} useFlexGap>
      {organization?.sandbox ? (
        <Tooltip
          title={intl.formatMessage(
            {
              id: 'features.pricing.sandbox.tooltip',
            },
            {
              boldText,
            }
          )}
        >
          <Chip
            label={
              <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
                <SvgIcon fontSize="small">
                  <IconTestPipe2 />
                </SvgIcon>
              </Stack>
            }
            color="info"
          />
        </Tooltip>
      ) : null}

      {isDevFeatureEnabled('indicator') ? (
        <Tooltip
          title={intl.formatMessage(
            {
              id: 'features.pricing.developerMode.tooltip',
            },
            { boldText }
          )}
        >
          <Chip
            label={
              <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
                <SvgIcon fontSize="small">
                  <IconFlask />
                </SvgIcon>
              </Stack>
            }
            color="warning"
          />
        </Tooltip>
      ) : null}
    </Stack>
  );

  const rightActions = (
    <>
      <SearchButton onClick={openSearch} />
      {isTrial && <CallToActionButton ctaLocation="navigation-panel" />}
      {hasCustomLogo && <PoweredBy />}
      <UserPanel />
    </>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <Slide in={isVisible} mountOnEnter unmountOnExit direction="down">
        <Box>
          <NavBar
            logo={<Logo />}
            tabs={tabs}
            currentTab={currentTab}
            onTabClick={handleTabClick}
            onTabHoverEnter={handleTabHoverEnter}
            onTabHoverLeave={handleMenuClose}
            badges={badges}
            rightActions={rightActions}
            hoverMenuSlot={
              <PopperMenu
                open={menuOpen}
                hoveredTab={hoveredTab}
                anchorEl={anchorEl}
                handleNavigation={handleNavigation}
              />
            }
          />
        </Box>
      </Slide>
      {isFeatureEnabled ? (
        <ToggleNavBarButton
          isVisible={isVisible}
          toggleVisibility={toggleVisibility}
        />
      ) : null}
      {bannerWarning ? (
        <PricingBanner
          warningText={bannerWarning}
          billingEnabled={billingEnabled}
        />
      ) : null}
    </Box>
  );
};

const Navbar = () => {
  const hideAppBar = useSelector(hideAppBarSelector);

  if (hideAppBar) {
    return null;
  }

  return <NavBarContent />;
};

export default Navbar;
